import { Injectable, OnDestroy } from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export interface ShortcutConfig {
  keys: string[]; // e.g. ['shift', 't']
  callback: () => void;
}

@Injectable({ providedIn: 'root' })
export class KeyboardShortcutService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private shortcuts: ShortcutConfig[] = [];
  private escapeCallbacks: Array<() => void> = [];

  constructor() {
    fromEvent<KeyboardEvent>(window, 'keydown')
      .pipe(takeUntil(this.destroy$))
      .subscribe((event) => this.handleKeydown(event));
  }

  /* ---------- PUBLIC API ---------- */

  registerShortcut(config: ShortcutConfig) {
    this.shortcuts.push(config);
  }

  unregisterShortcut(callback: () => void) {
    this.shortcuts = this.shortcuts.filter((s) => s.callback !== callback);
    this.escapeCallbacks = this.escapeCallbacks.filter((cb) => cb !== callback);
  }

  registerEscape(callback: () => void) {
    this.escapeCallbacks.push(callback);
  }

  unregisterEscape(callback: () => void) {
    this.escapeCallbacks = this.escapeCallbacks.filter((cb) => cb !== callback);
  }

  /* ---------- CORE LOGIC ---------- */

  private handleKeydown(event: KeyboardEvent) {
    // 🔐 ESC handling (highest priority)
    if (event.key === 'Escape') {
      this.escapeCallbacks.forEach((cb) => cb());
      return;
    }

    const pressedKeys = new Set<string>();

    if (event.shiftKey) pressedKeys.add('shift');
    if (event.ctrlKey) pressedKeys.add('ctrl');
    if (event.altKey) pressedKeys.add('alt');
    if (event.metaKey) pressedKeys.add('meta');

    const key = (event.key ?? '').toString().toLowerCase();
    if (key) pressedKeys.add(key);

    this.shortcuts.forEach((shortcut) => {
      const isMatch = shortcut.keys.every((k) => pressedKeys.has(k));
      if (isMatch) {
        event.preventDefault();
        shortcut.callback();
      }
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
